"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "How do I track my order status?",
  answer: "Once your order ships, you will receive a tracking number via email. You can use this number on our Order Tracking page to see real-time updates on your package's journey to your doorstep.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_tracking_update.png",
  imageAlt: "Smartphone showing order tracking status"
}, {
  question: "What is your return and exchange policy?",
  answer: "We offer a 30-day return window for all unworn items with original tags attached. Simply visit our Shipping & Returns page to initiate a return or exchange request through our automated portal.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_return_policy.png",
  imageAlt: "Fashion items neatly folded in a box"
}, {
  question: "How do I find my perfect size?",
  answer: "Each product page features a detailed size guide with specific measurements. We recommend comparing these to your favorite fitting garment to ensure the best possible fit for your new Lush Fashion pieces.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_size_guide.png",
  imageAlt: "Measuring tape on a fabric swatch"
}, {
  question: "Are your materials sustainably sourced?",
  answer: "Sustainability is at the heart of Lush Fashion. We partner with ethical manufacturers who prioritize eco-friendly fabrics and fair labor practices, ensuring your style choices support a better planet.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_sustainable_fabric.webp",
  imageAlt: "Close up of organic cotton fabric texture"
}, {
  question: "Can I update my account information?",
  answer: "Yes, you can manage your personal details, saved addresses, and payment methods at any time by logging into your My Account dashboard. Your data is encrypted and kept strictly confidential.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_account_settings.webp",
  imageAlt: "User profile settings on a laptop screen"
}, {
  question: "Do you offer international shipping?",
  answer: "We currently ship to over 50 countries worldwide. Shipping costs and estimated delivery times are calculated at checkout based on your destination and chosen shipping method.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_global_shipping.png",
  imageAlt: "World map with shipping routes highlighted"
}, {
  question: "How can I contact customer support?",
  answer: "Our dedicated support team is available Monday through Friday. You can reach us via the contact form on our Contact Us page or by emailing our support address directly for assistance.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_customer_support.png",
  imageAlt: "Customer service representative smiling"
}, {
  question: "How do I use my wishlist?",
  answer: "The wishlist allows you to save your favorite items for later. Simply click the heart icon on any product page to add it to your list, which you can access anytime from your account menu.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_wishlist_items.png",
  imageAlt: "Heart icon on a fashion product card"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="fashionfaq" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-serif font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                  § 05 / LUSH FASHION — CUSTOMER CARE
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="h-1 w-1 bg-primary" />
              </div>

              {}
              <h2 className="relative font-default font-semibold leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Frequently Asked{" "}
                <span className="italic text-primary">Questions</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Everything you need to know about shopping with Lush Fashion.
              </p>

              {}
              <Link to="/CustomerCarePage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary group">
                <span className="relative">
                  View Help Center
                  <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-primary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-semibold leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-primary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-foreground/15 aspect-[4/5] overflow-hidden rounded-md">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>

                            <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-primary" />
                <div className="h-px w-16 bg-primary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of questions
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}